import React, { useState, useEffect } from "react";
import { Button, Form, Select, Modal } from "semantic-ui-react";
import { connect } from "react-redux";
import { Spin } from "antd";
import GenerateMap from "./generate_map";
import { locus } from "../actions";

const CreateMap = ({ dataset, fetchdataset, createMap }) => {
  const [locations, setLocations] = useState([]);
  const [availableDatasets, setAvailableDatasets] = useState({});
  const [selectedVariable, setSelectedVariable] = useState("");
  const [selections, setSelections] = useState([]);
  const [distance, setDistance] = useState("1");
  const [portfolio, setPortfolio] = useState("");
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [apiError, setApiError] = useState("");

  // Fetch dataset when location changes
  useEffect(() => {
    if (locations.length > 0) {
      const formdata = new FormData();
      formdata.append("latitude", locations[0].latitude);
      formdata.append("longitude", locations[0].longitude);
      fetchdataset(formdata);
    }
  }, [locations, fetchdataset]);

  useEffect(() => {
    if (dataset) {
      setAvailableDatasets(dataset);
    }
  }, [dataset]);

  const handleMapDataChange = (newMapData) => {
    setLocations(newMapData);
  };
  const handleVariableChange = (e, { value }) => {
    console.log("value", value === "Historic Global Flood");
    setSelectedVariable(value);
    setSelections([]);

    if (value === "Historic Global Flood") {
      setDistance("30");
    } else {
      setDistance("1");
    }
  };

  const handleSelectChange = (e, { value }) => {
    setSelections(value);
  };

  const handleDistanceChange = (e, { value }) => {
    setDistance(value);
  };

  // Map UI value → backend key
  const getBackendVariableKey = (variable) => {
    if (variable === "Historic Global Flood") {
      return "global_observed_flood";
    }
    return variable.toLowerCase().replaceAll(" ", "_");
  };

  const isSubmitDisabled = () => {
    if (!locations || locations.length === 0) return true;
    if (!selectedVariable) return true;
    if (!portfolio || portfolio.trim().length < 3) return true;

    // Allow direct submit for Historic Global Flood
    if (selectedVariable === "Historic Global Flood") {
      return false;
    }

    if (selections.length === 0) return true;

    const key = getBackendVariableKey(selectedVariable);
    const ds = availableDatasets[key];

    return !(ds && Array.isArray(ds) && ds.length > 0);
  };

  const handleSubmit = () => {
    if (!locations || locations.length === 0) return;

    // This is only for dataset lookup
    const backendKey = getBackendVariableKey(selectedVariable);

    const formdata = new FormData();
    formdata.append("name", portfolio);
    formdata.append("address", locations[0].name);
    formdata.append("latitude", locations[0].latitude);
    formdata.append("longitude", locations[0].longitude);

    // ✅ IMPORTANT CHANGE HERE
    formdata.append(
      "portfolio_variables",
      JSON.stringify(
        selectedVariable === "Historic Global Flood"
          ? ["global_observed_flood"] // only this stays backend format
          : [selectedVariable], // send proper label like "Rainfall Flood"
      ),
    );

    formdata.append(
      "duration_data",
      selectedVariable === "Historic Global Flood"
        ? JSON.stringify([])
        : JSON.stringify(selections),
    );

    formdata.append(
      "distance",
      selectedVariable === "Historic Global Flood" ? "30" : distance,
    );

    setLoading(true);
    setApiError("");

    createMap(formdata)
      .then((res) => {
        if (res?.error) {
          setApiError(res.error);
        } else {
          setModalOpen(false);
        }
        setLoading(false);
      })
      .catch(() => {
        setApiError("Something went wrong. Please try again.");
        setLoading(false);
      });
  };

  const variableOptions = [
    { key: "Rainfall Flood", value: "Rainfall Flood", text: "Rainfall Flood" },
    { key: "Storm Surge", value: "Storm Surge", text: "Storm Surge" },
    { key: "Wildfire", value: "Wildfire", text: "Wildfire" },
    { key: "Extreme Heat", value: "Extreme Heat", text: "Extreme Heat" },
    { key: "Drought", value: "Drought", text: "Drought" },
    { key: "Sea Level Rise", value: "Sea Level Rise", text: "Sea Level Rise" },
    {
      key: "Historic Global Flood",
      value: "Historic Global Flood",
      text: "Historic Global Flood",
    },
  ];

  return (
    <div style={{ padding: "1.5rem" }}>
      <GenerateMap
        lat={39.0}
        long={34.0}
        zoom={1}
        onMapDataChange={handleMapDataChange}
      />

      <Button
        primary
        style={{
          marginTop: "1rem",
          float: "right",
          backgroundColor: "white",
          color: "#002060",
          border: "1px solid #002060",
        }}
        disabled={locations.length === 0}
        onClick={() => setModalOpen(true)}
      >
        PROCEED
      </Button>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Modal.Header>Create Map</Modal.Header>
        <Modal.Content>
          <Form style={{ margin: "20px auto" }}>
            <Form.Field>
              <p>Map Name</p>
              <Form.Input
                placeholder="Enter portfolio name"
                value={portfolio}
                onChange={(e) => setPortfolio(e.target.value)}
              />
            </Form.Field>

            <Form.Field>
              <p>Select Variable</p>
              <Select
                placeholder="Select Variable"
                options={variableOptions.filter((opt) => {
                  if (opt.value === "Historic Global Flood") return true;

                  const key = opt.value.toLowerCase().replaceAll(" ", "_");
                  const ds = availableDatasets[key];

                  return ds && Array.isArray(ds) && ds.length > 0;
                })}
                value={selectedVariable}
                onChange={handleVariableChange}
              />
            </Form.Field>

            {selectedVariable && selectedVariable !== "Historic Global Flood" && (
              <Form.Field>
                <p>Available Datasets</p>
                <Select
                  placeholder="Select Datasets"
                  multiple
                  options={
                    availableDatasets[
                      getBackendVariableKey(selectedVariable)
                    ]?.map((option) => ({
                      key: option.value,
                      value: option.value,
                      text: option.key,
                    })) || []
                  }
                  value={selections}
                  onChange={handleSelectChange}
                />
              </Form.Field>
            )}

            {selectedVariable !== "Historic Global Flood" &&
              selections.length > 0 && (
                <Form.Field>
                  <p>Distance (km)</p>
                  <Select
                    options={["1", "3", "5"].map((d) => ({
                      key: d,
                      value: d,
                      text: `${d} km`,
                    }))}
                    value={distance}
                    onChange={handleDistanceChange}
                  />
                </Form.Field>
              )}

            {apiError && (
              <div style={{ color: "red", marginBottom: "1rem" }}>
                {apiError}
              </div>
            )}

            {loading ? (
              <div style={{ textAlign: "center", marginTop: "1rem" }}>
                <Spin tip="Generating Map..." size="large" />
              </div>
            ) : (
              <Button
                primary
                disabled={isSubmitDisabled()}
                onClick={handleSubmit}
                style={{
                  marginTop: "1rem",
                  backgroundColor: "white",
                  color: "#002060",
                  border: "1px solid #002060",
                }}
              >
                Submit
              </Button>
            )}
          </Form>
        </Modal.Content>
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  dataset: state.locus.dataset,
});

const mapDispatchToProps = (dispatch) => ({
  fetchdataset: (formdata) => dispatch(locus.Dataset(formdata)),
  createMap: (formdata) => dispatch(locus.createMap(formdata)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateMap);
